import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase.js';

const AUTO_REFRESH_MS = 60_000;

const ADMIN_TOOLS = [
  { path: '/admin-users', title: 'Manage Users', description: 'Owner accounts and admin access' },
  { path: '/admin-invoices', title: 'Manage Invoices', description: 'HOA billing and Stripe' },
  { path: '/admin-finance', title: 'HOA Finance', description: 'Bank accounts, vendor bills, contractor payments' },
  { path: '/admin-compliance', title: 'Governance & Compliance', description: 'Meetings, elections, legal checklist' },
  { path: '/admin-polls', title: 'Manage Polls', description: 'Open and close community polls' },
  { path: '/admin-mailbox', title: 'Mailbox Setup', description: 'Ring devices and detection' },
  { path: '/vehicle-log', title: 'Vehicle Log', description: 'Sightings and gate assignment' },
  { path: '/violations', title: 'Violations', description: 'Record and resolve lot violations' },
  { path: '/emergency-message', title: 'Emergency Message', description: 'Community-wide alert banner' },
  { path: '/developer-guide', title: 'Developer Guide', description: 'Architecture and deployment reference' },
  { path: '/community-services', title: 'Community Services', description: 'Work orders, amenities, visitors, parking, marketplace, e-sign' },
  { path: '/admin-sms', title: 'SMS Alerts', description: 'Twilio urgent text broadcasts' },
];

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function SeverityBadge({ severity }) {
  return <span className={`admin-monitor-badge admin-monitor-badge--${severity || 'info'}`}>{severity || 'info'}</span>;
}

function PanelToolbar({ children, onRefresh, loading, label }) {
  return (
    <div className="admin-monitor-toolbar">
      <p className="admin-monitor-toolbar-lead">{label}</p>
      <button type="button" className="admin-portal-btn" disabled={loading} onClick={onRefresh}>
        {loading ? 'Refreshing…' : 'Refresh'}
      </button>
      {children}
    </div>
  );
}

function OverviewPanel({ snapshot, loading, onRefresh }) {
  if (!snapshot && loading) return <p>Loading platform overview…</p>;
  if (!snapshot) return <p className="admin-monitor-error">Could not load overview.</p>;

  const healthScore = Math.max(
    0,
    Math.min(
      100,
      100
        - snapshot.signals.filter((s) => s.severity === 'warning').length * 12
        - snapshot.signals.filter((s) => s.severity === 'critical').length * 25,
    ),
  );

  return (
    <div className="admin-monitor-panel">
      <PanelToolbar label="Live snapshot from Firebase, Ring, gate, and vehicle pipelines." onRefresh={onRefresh} loading={loading} />
      <div className="admin-stat-grid admin-monitor-stat-grid">
        <article className="admin-stat-card admin-stat-card--accent">
          <span className="admin-stat-label">Platform health</span>
          <strong className="admin-stat-value">{healthScore}</strong>
          <span className="admin-stat-hint">Based on open warning signals</span>
        </article>
        <article className="admin-stat-card">
          <span className="admin-stat-label">Owner lots</span>
          <strong className="admin-stat-value">{snapshot.overview.ownerLots}</strong>
        </article>
        <article className="admin-stat-card">
          <span className="admin-stat-label">Platform admins</span>
          <strong className="admin-stat-value">{snapshot.overview.platformAdmins}</strong>
        </article>
        <article className="admin-stat-card">
          <span className="admin-stat-label">Open polls</span>
          <strong className="admin-stat-value">{snapshot.overview.openPolls}</strong>
        </article>
      </div>
      <div className="admin-monitor-signals">
        {snapshot.signals.map((signal) => (
          <span key={signal.id} className={`admin-monitor-signal admin-monitor-signal--${signal.severity}`}>
            <strong>{signal.label}</strong> {signal.value}
          </span>
        ))}
      </div>
      <p className="admin-monitor-meta">Updated {formatTime(snapshot.generatedAt)} · Project {snapshot.projectId}</p>
    </div>
  );
}

function OpsPanel({ snapshot, loading, onRefresh, onRunScan, scanLoading, actionMessage }) {
  if (!snapshot && loading) return <p>Loading Firebase ops…</p>;
  if (!snapshot) return <p className="admin-monitor-error">Could not load ops snapshot.</p>;

  const logs = snapshot.recentLogs?.entries ?? [];

  return (
    <div className="admin-monitor-panel">
      <PanelToolbar
        label="Firestore counts, Cloud Logging tail, daily integrity scan, and GCP console links."
        onRefresh={onRefresh}
        loading={loading}
      >
        <button type="button" className="admin-portal-btn admin-portal-btn-primary" disabled={scanLoading} onClick={onRunScan}>
          {scanLoading ? 'Running scan…' : 'Run daily scan'}
        </button>
      </PanelToolbar>
      {actionMessage && <div className="admin-portal-alert" role="status">{actionMessage}</div>}

      <div className="admin-portal-panel">
        <header className="admin-portal-panel-header"><h3>Firestore collections (sampled)</h3></header>
        <div className="admin-monitor-table-wrap">
          <table className="admin-monitor-table">
            <thead><tr><th>Collection</th><th>Docs (sample)</th><th>Notes</th></tr></thead>
            <tbody>
              {snapshot.firestore.collections.map((row) => (
                <tr key={row.name}>
                  <td><code>{row.name}</code></td>
                  <td>{row.count ?? '—'}</td>
                  <td>{row.truncated ? 'Truncated at sample limit' : row.error || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {snapshot.dailyScan?.lastReport && (
        <div className="admin-portal-panel">
          <header className="admin-portal-panel-header">
            <h3>Last daily scan</h3>
            <span className="admin-portal-badge">{snapshot.dailyScan.lastReport.summary?.total ?? 0} findings</span>
          </header>
          <ul className="admin-monitor-findings">
            {snapshot.dailyScan.lastReport.findings?.map((f) => (
              <li key={f.id} className={`admin-monitor-finding admin-monitor-finding--${f.severity}`}>
                <strong>{f.title}</strong> — {f.detail}
                <div className="admin-monitor-finding-fix">{f.remediation}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="admin-monitor-split">
        <div className="admin-portal-panel admin-monitor-panel-wide">
          <header className="admin-portal-panel-header"><h3>Recent warnings & errors</h3></header>
          {snapshot.recentLogs?.unavailable ? (
            <p className="admin-monitor-hint">{snapshot.recentLogs.hint}</p>
          ) : null}
          <div className="admin-monitor-table-wrap">
            <table className="admin-monitor-table">
              <thead><tr><th>Time</th><th>Sev</th><th>Resource</th><th>Message</th></tr></thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={4}>No warning-level log entries in the last {snapshot.recentLogs?.lookbackHours ?? 2}h.</td></tr>
                ) : logs.map((entry, idx) => (
                  <tr key={`${entry.timestamp}-${idx}`}>
                    <td>{formatTime(entry.timestamp)}</td>
                    <td>{entry.severity}</td>
                    <td><code>{entry.resource}</code></td>
                    <td className="admin-monitor-log-msg">{entry.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="admin-portal-panel">
          <header className="admin-portal-panel-header"><h3>Debug links</h3></header>
          <ul className="admin-link-grid">
            {snapshot.debugLinks.map((link) => (
              <li key={link.href}>
                <a href={link.href} className="admin-link-card" target="_blank" rel="noreferrer">
                  <strong>{link.title}</strong>
                  <span>Open in Google Cloud Console</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ScheduledJobsPanel({ snapshot, loading, onRefresh }) {
  if (!snapshot && loading) return <p>Loading scheduled jobs…</p>;
  if (!snapshot) return <p className="admin-monitor-error">Could not load scheduled jobs.</p>;

  return (
    <div className="admin-monitor-panel">
      <PanelToolbar label="Background Cloud Scheduler / Firebase scheduled functions." onRefresh={onRefresh} loading={loading} />
      <div className="admin-monitor-table-wrap">
        <table className="admin-monitor-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Schedule</th>
              <th>Last run</th>
              <th>Status</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.jobs.map((job) => {
              const last = job.lastRun;
              const status = last?.lastStatus ?? 'unknown';
              return (
                <tr key={job.id}>
                  <td>
                    <strong>{job.name}</strong>
                    <div><code>{job.functionName}</code></div>
                  </td>
                  <td>{job.schedule}</td>
                  <td>{formatTime(last?.lastRunAt)}</td>
                  <td><span className={`admin-scheduled-status admin-scheduled-status--${status}`}>{status}</span></td>
                  <td>{last?.lastSummary || last?.lastError || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WatcherPanel({ incidents, loading, onRefresh, onRunCheck, onResolve, checkLoading, actionLoading }) {
  const [selectedId, setSelectedId] = useState(null);
  const selected = incidents.find((i) => i.id === selectedId) ?? incidents[0];

  return (
    <div className="admin-monitor-panel">
      <PanelToolbar
        label="Automated incident detection from ops signals (Ring, gate, mailbox, polls)."
        onRefresh={onRefresh}
        loading={loading}
      >
        <button type="button" className="admin-portal-btn admin-portal-btn-primary" disabled={checkLoading} onClick={onRunCheck}>
          {checkLoading ? 'Running…' : 'Run check now'}
        </button>
      </PanelToolbar>
      <div className="admin-monitor-split">
        <div className="admin-portal-panel">
          <header className="admin-portal-panel-header">
            <h3>Incidents</h3>
            <span className="admin-portal-badge">{incidents.filter((i) => i.status === 'open').length} open</span>
          </header>
          <ul className="admin-monitor-incident-list">
            {incidents.length === 0 ? (
              <li className="admin-monitor-empty">No incidents recorded. Run a check to evaluate current signals.</li>
            ) : incidents.map((inc) => (
              <li key={inc.id}>
                <button
                  type="button"
                  className={`admin-monitor-incident${selected?.id === inc.id ? ' admin-monitor-incident--active' : ''}${inc.status === 'open' ? ` admin-monitor-incident--${inc.severity}` : ''}`}
                  onClick={() => setSelectedId(inc.id)}
                >
                  <span className="admin-monitor-incident-title">{inc.title}</span>
                  <SeverityBadge severity={inc.severity} />
                  <span className="admin-monitor-incident-meta">{inc.status} · {formatTime(inc.updatedAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="admin-portal-panel">
          <header className="admin-portal-panel-header"><h3>Detail</h3></header>
          {selected ? (
            <>
              <p>{selected.summary}</p>
              <p className="admin-monitor-meta">Source: {selected.source}</p>
              {selected.status === 'open' && (
                <button
                  type="button"
                  className="admin-portal-btn"
                  disabled={actionLoading}
                  onClick={() => onResolve(selected.id)}
                >
                  Mark resolved
                </button>
              )}
            </>
          ) : (
            <p className="admin-monitor-empty">Select an incident</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ApiCatalogPanel({ catalog, loading, onRefresh }) {
  if (!catalog && loading) return <p>Loading API catalog…</p>;
  if (!catalog) return <p className="admin-monitor-error">Could not load API catalog.</p>;

  return (
    <div className="admin-monitor-panel">
      <PanelToolbar label="Callable, HTTP, and scheduled surfaces deployed for this community portal." onRefresh={onRefresh} loading={loading} />
      <div className="admin-stat-grid admin-monitor-stat-grid">
        <article className="admin-stat-card"><span className="admin-stat-label">Total</span><strong className="admin-stat-value">{catalog.summary.total}</strong></article>
        <article className="admin-stat-card"><span className="admin-stat-label">In use</span><strong className="admin-stat-value">{catalog.summary.inUse}</strong></article>
        <article className="admin-stat-card"><span className="admin-stat-label">Callables</span><strong className="admin-stat-value">{catalog.summary.callables}</strong></article>
        <article className="admin-stat-card"><span className="admin-stat-label">Scheduled</span><strong className="admin-stat-value">{catalog.summary.scheduled}</strong></article>
      </div>
      <div className="admin-monitor-table-wrap">
        <table className="admin-monitor-table">
          <thead><tr><th>Name</th><th>Kind</th><th>Section</th><th>Status</th><th>Description</th></tr></thead>
          <tbody>
            {catalog.surfaces.map((s) => (
              <tr key={s.name}>
                <td><code>{s.name}</code></td>
                <td>{s.kind}</td>
                <td>{s.section}</td>
                <td>{s.inUse ? 'In use' : 'Debug / unused'}</td>
                <td>{s.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ToolsPanel() {
  return (
    <section className="admin-portal-panel">
      <header className="admin-portal-panel-header">
        <h3>Admin tools</h3>
        <span className="admin-portal-badge">{ADMIN_TOOLS.length} destinations</span>
      </header>
      <ul className="admin-link-grid">
        {ADMIN_TOOLS.map((tool) => (
          <li key={tool.path}>
            <Link to={tool.path} className="admin-link-card">
              <strong>{tool.title}</strong>
              <span>{tool.description}</span>
              <code className="admin-code">{tool.path}</code>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function useAdminMonitoring(activeSection) {
  const [opsSnapshot, setOpsSnapshot] = useState(null);
  const [scheduledSnapshot, setScheduledSnapshot] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [apiCatalog, setApiCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [error, setError] = useState('');

  const loadOps = useCallback(async () => {
    const fn = httpsCallable(functions, 'getAdminOpsSnapshot');
    const result = await fn();
    setOpsSnapshot(result.data.snapshot);
    return result.data.snapshot;
  }, []);

  const loadScheduled = useCallback(async () => {
    const fn = httpsCallable(functions, 'getScheduledJobsStatus');
    const result = await fn();
    setScheduledSnapshot(result.data.snapshot);
  }, []);

  const loadIncidents = useCallback(async () => {
    const fn = httpsCallable(functions, 'getWatcherIncidents');
    const result = await fn();
    setIncidents(result.data.incidents ?? []);
  }, []);

  const loadCatalog = useCallback(async () => {
    const fn = httpsCallable(functions, 'getAdminApiCatalog');
    const result = await fn();
    setApiCatalog(result.data);
  }, []);

  const refreshSection = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (activeSection === 'overview' || activeSection === 'ops') await loadOps();
      if (activeSection === 'scheduled') await loadScheduled();
      if (activeSection === 'watcher') await loadIncidents();
      if (activeSection === 'api-catalog') await loadCatalog();
    } catch (err) {
      setError(err.message || 'Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  }, [activeSection, loadOps, loadScheduled, loadIncidents, loadCatalog]);

  useEffect(() => {
    if (activeSection === 'tools') return undefined;
    refreshSection();
    const timer = setInterval(refreshSection, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [activeSection, refreshSection]);

  const runDailyScan = async () => {
    setScanLoading(true);
    setActionMessage('');
    try {
      const fn = httpsCallable(functions, 'runDailyScan');
      const result = await fn();
      setActionMessage(`Daily scan complete — ${result.data.report?.findings?.length ?? 0} findings.`);
      await loadOps();
    } catch (err) {
      setActionMessage(err.message || 'Daily scan failed');
    } finally {
      setScanLoading(false);
    }
  };

  const runWatcherCheck = async () => {
    setCheckLoading(true);
    try {
      const fn = httpsCallable(functions, 'runWatcherCheckFn');
      const result = await fn();
      setIncidents(result.data.incidents ?? []);
    } catch (err) {
      setError(err.message || 'Watcher check failed');
    } finally {
      setCheckLoading(false);
    }
  };

  const resolveIncident = async (incidentId) => {
    setActionLoading(true);
    try {
      const fn = httpsCallable(functions, 'resolveWatcherIncident');
      await fn({ incidentId });
      await loadIncidents();
    } catch (err) {
      setError(err.message || 'Could not resolve incident');
    } finally {
      setActionLoading(false);
    }
  };

  return {
    opsSnapshot,
    scheduledSnapshot,
    incidents,
    apiCatalog,
    loading,
    scanLoading,
    checkLoading,
    actionLoading,
    actionMessage,
    error,
    refreshSection,
    runDailyScan,
    runWatcherCheck,
    resolveIncident,
  };
}

export function AdminMonitoringSection({ section, monitoring }) {
  const {
    opsSnapshot,
    scheduledSnapshot,
    incidents,
    apiCatalog,
    loading,
    scanLoading,
    checkLoading,
    actionLoading,
    actionMessage,
    error,
    refreshSection,
    runDailyScan,
    runWatcherCheck,
    resolveIncident,
  } = monitoring;

  if (error) {
    return <div className="admin-portal-alert admin-monitor-error-banner">{error}</div>;
  }

  switch (section) {
    case 'overview':
      return <OverviewPanel snapshot={opsSnapshot} loading={loading} onRefresh={refreshSection} />;
    case 'tools':
      return <ToolsPanel />;
    case 'ops':
      return (
        <OpsPanel
          snapshot={opsSnapshot}
          loading={loading}
          onRefresh={refreshSection}
          onRunScan={runDailyScan}
          scanLoading={scanLoading}
          actionMessage={actionMessage}
        />
      );
    case 'scheduled':
      return <ScheduledJobsPanel snapshot={scheduledSnapshot} loading={loading} onRefresh={refreshSection} />;
    case 'watcher':
      return (
        <WatcherPanel
          incidents={incidents}
          loading={loading}
          onRefresh={refreshSection}
          onRunCheck={runWatcherCheck}
          onResolve={resolveIncident}
          checkLoading={checkLoading}
          actionLoading={actionLoading}
        />
      );
    case 'api-catalog':
      return <ApiCatalogPanel catalog={apiCatalog} loading={loading} onRefresh={refreshSection} />;
    default:
      return null;
  }
}

export const ADMIN_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'tools', label: 'Tools' },
  { id: 'ops', label: 'Firebase Ops' },
  { id: 'scheduled', label: 'Scheduled jobs' },
  { id: 'watcher', label: 'Watcher' },
  { id: 'api-catalog', label: 'API catalog' },
];